import { inject, Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { CaptchaChallengePopupComponent } from "@shared/components/captcha-challenge-popup/captcha-challenge-popup.component";
import { Recaptcha } from "../keys-recaptcha/recaptcha/Recaptcha.interface";

@Injectable({
    providedIn: 'root'
})
export class RecaptchaIntegrator {
    private sitekey: string;
    private token: string;
    private type: string;
    private dialog = inject(MatDialog);
    private expiredToken: Boolean;

    public setValues(sitekey: string, type: string): void {
        this.sitekey = sitekey;
        this.type = type;
    }

    public async validate() {
        if (this.type === 'invisible') {
            await this.loadScript();
            return await this.validateInvisible();
        }
        if (this.type === 'checkbox') {
            await this.loadScript();
            return await this.validateV2();
        }
        if (this.type === 'v3') {
            return await this.validateV3();
        }
    }

    clear(): void {
        this.sitekey = null;
        this.type = null;
    }

    removeScript(): void {
        const scripts = document.querySelectorAll('.ps-recaptcha-script');
        scripts.forEach((script: HTMLScriptElement) => {
            if (script.parentNode) {
                script.parentNode.removeChild(script);
            }
        });

        const widgets = document.getElementById("widgets");
        if (widgets) {
            widgets.remove();
        }

        const anchors = document.querySelectorAll('.rc-anchor');
        anchors.forEach((anchor: HTMLElement) => { 
            if (anchor.parentNode) {
                anchor.parentNode.removeChild(anchor);
            }
        });

        const badges = document.querySelectorAll('.grecaptcha-badge');
        badges.forEach((badge: HTMLElement) => {
            if (badge.parentNode) {
                badge.parentNode.removeChild(badge);
            }
        });
    }

    public renderV2(container: Element, baseOptions: any) {
        if (!container) {
            return;
        }
        baseOptions = baseOptions || {
            sitekey: this.sitekey,
            'expired-callback': () => this.expired(),
            'error-callback': () => this.errored([]),
            callback: (token: string) => this.resolved(token),
        };
        window['grecaptcha'].ready(() => {
            window['grecaptcha'].render(container, baseOptions);
        });
    }

    private validateInvisible(): Promise<string> {
        return new Promise((resolve, reject) => {
            const element = this.createElement(true);
            window['grecaptcha'].render(element, {
                sitekey: this.sitekey,
                size: 'invisible',
                callback: (token: string) => {
                    this.setToken(token);
                    resolve(token);
                },
                'error-callback': (error: any) => {
                    this.reset();
                    reject(error);
                },
            });

            window['grecaptcha']
                .execute()
                .catch((error: any) => {
                    this.reset();
                    reject(error);
                });
        });
    }

    private async validateV2(): Promise<string> {
        return new Promise((resolve, reject) => {
            this.dialog.open(CaptchaChallengePopupComponent, {
                width: '364px',
                minHeight: '186px',
                autoFocus: false,
                data: {
                    message: "Clique para verificar as credenciais.",
                    sitekey: this.sitekey
                },
                disableClose: false
            }).afterClosed().subscribe(token => {
                if (token) {
                    resolve(token);
                } else {
                    reject();
                }
            });
        });
    }

    private async validateV3(): Promise<string> {
        await this.loadScript();
        return this.withTimeout(new Promise<string>((resolve, reject) => {
            window['grecaptcha'].ready(() => {
                window['grecaptcha']
                    .execute(this.sitekey)
                    .then((token: string) => resolve(token))
                    .catch(err => reject(err));
            });
        }), 8000);
    }

    public async renderV3(): Promise<void> {
        return this.loadScript();
    }

    public renderInvisible(element: Element): void {
        element = element ? element : this.createElement(false);
        window['grecaptcha'].render(element, {
            sitekey: this.sitekey,
            size: 'invisible',
            callback: (token: string) => this.setToken(token),
            'error-callback': (error: any) => this.errored(error)
        });
    }

    public async generateToken(): Promise<string> {
        if (this.getType() == 'invisible') {
            return new Promise((resolve, reject) => {
                window['grecaptcha'].ready(() => {
                    window['grecaptcha']
                        .execute();
                });
                this.waitForToken().then((token) => {
                    this.setToken(token);
                    resolve(token);
                }).catch(() => {
                    reject();
                })
            })
        }

        if (this.getType() == 'v3') {
            return new Promise((resolve, reject) => {
                window['grecaptcha'].ready(() => {
                    window['grecaptcha']
                        .execute(this.sitekey)
                        .then((token: string) => {
                            this.setToken(token);
                            resolve(token);
                        })
                        .catch(() => {
                            reject();
                        })
                });
            })
        }
    }

    waitForToken() {
        return new Promise<string>((resolve, reject) => {
            const checkInterval = 100;
            const timeout = 5000;

            const interval = setInterval(() => {
                if (this.token) {
                    clearInterval(interval);
                    clearTimeout(timeoutId);
                    resolve(this.token);
                }
            }, checkInterval);

            const timeoutId = setTimeout(() => {
                clearInterval(interval);
                reject();
            }, timeout);
        });
    }

    private withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> => {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(), ms);
            promise
                .then(result => {
                    clearTimeout(timeout);
                    resolve(result);
                })
                .catch(() => {
                    clearTimeout(timeout);
                    reject();
                });
        });
    };

    private createElement(ocult = false): HTMLElement {
        const widgets = document.getElementById("widgets");
        const existingWidget = widgets.querySelector('.recaptcha-hidden-widget');
        if (existingWidget) {
            existingWidget.remove();
        }
        const widget = document.createElement('div');
        widgets.appendChild(widget);
        widget.classList.add('recaptcha-hidden-widget');
        if (ocult) {
            widget.style.visibility = 'hidden';
            widget.style.position = 'absolute';
            widget.style.top = '-9999px';
        }
        return widget;
    }

    private resolved(token: string): void {
        this.setToken(token);
    }

    private errored(errors): void {
        (window as any).err_login_captcha = true;
        console.error("Error rendering captcha:", errors);
    }

    private getType() {
        return this.type;
    }

    getToken(): string {
        return this.token;
    }

    private setToken(value: string): void {
        this.token = value
    }

    private expired() {
        this.expiredToken = true;
    }

    public getExpired(): boolean {
        return this.expiredToken ? true : false
    }

    reset(): void {
        window['grecaptcha'].reset();
    }

    usingCaptcha(recaptcha: Recaptcha, type: String = '') {
        if (recaptcha && recaptcha?.active && recaptcha?.captcha_portal_active) {
            if (type) {
                if (recaptcha.type == type) {
                    return true;
                } else {
                    return false
                }
            }
            return true;
        }
        return false;
    }

    waitForGrecaptchaReady(resolve: () => void, reject: (err?: any) => void) {
        const maxRetries = 40;
        let attempts = 0;

        const interval = setInterval(() => {
            if (window['grecaptcha'] && typeof window['grecaptcha'].ready === 'function') {
                clearInterval(interval);
                resolve();
            } else if (++attempts >= maxRetries) {
                clearInterval(interval);
                reject();
            }
        }, 100);
    }

    private async loadScript(): Promise<void> {
        return new Promise((resolve, reject) => {
            const src = this.type === 'checkbox' || this.type === 'invisible'
                ? 'https://www.google.com/recaptcha/api.js'
                : `https://www.google.com/recaptcha/api.js?render=${this.sitekey}`;

            const script = document.createElement('script');
            script.src = src;
            script.className = 'ps-recaptcha-script'
            script.async = true;
            script.defer = true;

            script.onload = () => {
                this.waitForGrecaptchaReady(resolve, reject);
            };

            script.onerror = (err) => {
                console.error('Erro ao carregar o script do reCAPTCHA:', err);
                reject(err);
            };

            document.head.appendChild(script);
        });
    }
}
